import type { JSX } from 'solid-js'
import { createMemo, mergeProps, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import type { ChipColorType, ChipPropsType, ChipSizeType, ChipVariantType } from '~/types/Chip'

const variantClasses: Record<ChipVariantType, Record<ChipColorType, string>> = {
  filled: {
    primary: 'bg-primary text-white',
    secondary: 'bg-slate-500 text-white dark:bg-slate-400',
    success: 'bg-success text-white',
    danger: 'bg-danger text-white',
    warning: 'bg-warning text-white',
    info: 'bg-info text-white',
    default: 'bg-inverse text-on-inverse'
  },
  outlined: {
    primary: 'border border-primary text-primary',
    secondary: 'border border-slate-500 text-foreground-subtle dark:border-slate-400',
    success: 'border border-success text-success',
    danger: 'border border-danger text-danger',
    warning: 'border border-warning text-warning',
    info: 'border border-info text-info',
    default: 'border border-slate-800 text-foreground dark:border-slate-200'
  },
  soft: {
    primary: 'bg-primary-50 text-primary-800 dark:bg-primary-800/50 dark:text-primary-100',
    secondary: 'bg-slate-50 text-foreground dark:bg-slate-700/60',
    success: 'bg-green-50 text-green-800 dark:bg-green-800/50 dark:text-green-100',
    danger: 'bg-red-50 text-red-800 dark:bg-red-800/50 dark:text-red-100',
    warning: 'bg-amber-50 text-amber-800 dark:bg-amber-800/50 dark:text-amber-100',
    info: 'bg-blue-50 text-blue-800 dark:bg-blue-800/50 dark:text-blue-100',
    default: 'bg-slate-50 text-foreground dark:bg-slate-700/60'
  }
}

const sizeClasses: Record<ChipSizeType, { base: string; close: string }> = {
  xs: { base: 'text-[10px] leading-4 px-1.5 py-0.5 gap-1', close: 'w-3 h-3 p-0.5' },
  sm: { base: 'text-xs leading-5 px-2 py-0.5 gap-1', close: 'w-3.5 h-3.5 p-0.5' },
  md: { base: 'text-sm leading-6 px-2.5 py-1 gap-1.5', close: 'w-4 h-4 p-0.5' },
  lg: { base: 'text-base leading-7 px-3 py-1.5 gap-2', close: 'w-5 h-5 p-0.5' },
  xl: { base: 'text-lg leading-8 px-3.5 py-2 gap-2', close: 'w-6 h-6 p-0.5' }
}

export default function Chip(props: ChipPropsType): JSX.Element {
  const [local, others] = splitProps(props, ['color', 'size', 'variant', 'removable', 'disabled', 'avatar', 'icon', 'iconColor', 'iconSize', 'rounded', 'onRemove', 'class', 'classList', 'children'])

  const merged = createMemo(() =>
    mergeProps(
      {
        color: 'primary' as ChipColorType,
        size: 'md' as ChipSizeType,
        variant: 'filled' as ChipVariantType,
        removable: false,
        disabled: false,
        avatar: undefined,
        icon: undefined,
        iconColor: undefined,
        iconSize: undefined,
        rounded: false
      },
      local
    )
  )

  const classes = createMemo(() => {
    const variant = variantClasses[merged().variant][merged().color]
    const size = sizeClasses[merged().size]

    return twMerge(
      [
        'inline-flex items-center',
        merged().rounded ? 'rounded-full' : 'rounded-lg',
        'transition-colors duration-200',
        size.base,
        merged().disabled ? 'opacity-50 cursor-not-allowed' : '',
        variant,
        merged().class
      ].filter(Boolean)
    )
  })

  const buttonClasses = createMemo(() => {
    const size = sizeClasses[merged().size]
    return twMerge(['inline-flex items-center justify-center flex-shrink-0', 'rounded-full', 'hover:bg-black/10 dark:hover:bg-white/10', 'focus:outline-none', size.close])
  })

  return (
    <span {...others} class={classes()} classList={merged().classList} role="status" aria-disabled={merged().disabled}>
      {merged().avatar && <img src={merged().avatar} alt="Avatar" class="w-6 h-6 rounded-full mr-2" />}

      <Show when={merged().icon}>
        <Icon icon={merged().icon} size={merged().iconSize || 20} class={merged().iconColor} />
      </Show>

      {merged().children}
      {merged().removable && !merged().disabled && (
        <button
          type="button"
          class={buttonClasses()}
          onClick={e => {
            e.stopPropagation()
            merged().onRemove?.(e)
          }}
          aria-label="Remove item"
        >
          <Icon icon="material-symbols-light:close" size={16} />
          <span class="sr-only">Remove</span>
        </button>
      )}
    </span>
  )
}
